import moment from 'moment';
import get from 'lodash/get';
import {useEffect,useState} from 'react';

import AppDashboard from '../AppDashboard';
import ProviderHomeScreen from '../../components/providers/ProviderHomeScreen';
import {useGetAppointmentsQuery} from '../../redux/services/appointmentApi';
import {useGetCurrentProviderQuery} from '../../redux/services/providerApi';
import {useGetLeadsQuery} from '../../redux/services/leadApi';
import {useGetMessagesQuery} from '../../redux/services/messageApi';
import {useGetPatientsQuery} from '../../redux/services/patientApi';
import {useGetSessionsQuery} from '../../redux/services/sessionApi';

const getMetricsRange = ({metricsPeriod}) => {
  if (metricsPeriod === 'biweek') {
    const startTime = moment().startOf('week').subtract(7, 'day').toISOString();
    const endTime = moment().toISOString();
    const startPriorPeriodTime = moment()
      .startOf('week')
      .subtract(3, 'weeks')
      .toISOString();
    const endPriorPeriodTime = startTime;
    return {startTime, endTime, startPriorPeriodTime, endPriorPeriodTime};
  }
  if (['7day', '14day', '30day'].includes(metricsPeriod)) {
    let days;
    if (metricsPeriod === '7day') days = 7;
    if (metricsPeriod === '14day') days = 14;
    if (metricsPeriod === '30day') days = 30;
    const startTime = moment().startOf('day').subtract(days, 'day').toISOString();
    const endTime = moment().endOf('day').toISOString();
    const startPriorPeriodTime = moment()
      .subtract(days * 2, 'days')
      .startOf('day')
      .toISOString();
    const endPriorPeriodTime = startTime;
    return {startTime, endTime, startPriorPeriodTime, endPriorPeriodTime};
  }
  let periodStr;
  if (metricsPeriod === 'month') periodStr = 'month';
  if (metricsPeriod === 'week') periodStr = 'week';
  // if (metricsPeriod === 'day') periodStr = 'day';
  if (!periodStr) return null;
  const startTime = moment().startOf(periodStr).toISOString();
  const endTime = moment().toISOString();
  const startPriorPeriodTime = moment()
    .subtract(1, `${periodStr}s`)
    .startOf(periodStr)
    .toISOString();
  const endPriorPeriodTime = moment()
    .subtract(1, `${periodStr}s`)
    .endOf(periodStr)
    .toISOString();
  return {startTime, endTime, startPriorPeriodTime, endPriorPeriodTime};
};

const getSessionGoal = ({metricsPeriod, provider}) => {
  const goal = get(provider, 'weekly_session_goal') || 20;
  switch (metricsPeriod) {
    case 'month':
      return goal * 4;
    case 'week':
      return goal;
    case 'biweek':
      return goal * 2;
    case '30day':
      return goal * 4;
    case '14day':
      return goal * 2;
    case '7day':
      return goal;
    default:
      break;
  }
}

const useMetricsRange = ({metricsPeriod, setMetricsRange}) => {
  useEffect(() => {
    const range = getMetricsRange({metricsPeriod});
    if (range) {
      setMetricsRange(range);
    }
  }, [metricsPeriod]);
};

const ProviderHomePage = props => {
  const [metricsPeriod, setMetricsPeriod] = useState('month');
  const [metricsRange, setMetricsRange] = useState(null);
  const [showTomorrow, setShowTomorrow] = useState(false);
  useMetricsRange({metricsPeriod, setMetricsRange});
  const appointmentsQuery = useGetAppointmentsQuery({
    start_time: moment().set({hour: 0, minute: 0, second: 0, millisecond: 0}).toISOString(),
    end_time: moment().set({hour: 23, minute: 59, second: 59, millisecond: 0}).toISOString(),
  });

  const providerQuery = useGetCurrentProviderQuery({skip: false});
  const provider = get(providerQuery, 'data');
  const messagesQuery = useGetMessagesQuery({
    is_read: false,
    user_to_id: providerQuery?.data?.user_id,
  }, {skip: !providerQuery?.data?.user_id});

  const patientsQuery =  useGetPatientsQuery({patient_status: 'Unscheduled'});
  const todaySessionQuery = useGetSessionsQuery({
    start_time: moment().startOf('day').toISOString(),
    end_time: moment().endOf('day').toISOString(),
  });
  const incompleteSessionsQuery = useGetSessionsQuery({action_is_needed: true});
  const sessionMetricsQuery = useGetSessionsQuery({
    is_completed: true,
    start_time: metricsRange?.startTime,
    end_time: metricsRange?.endTime,
  }, {skip: !metricsRange});

  const sessionMetricsPriorPeriodQuery = useGetSessionsQuery({
    is_completed: true,
    start_time: metricsRange?.startPriorPeriodTime,
    end_time: metricsRange?.endPriorPeriodTime,
  }, {skip: !metricsRange});

  const todaySessionsQuery = useGetSessionsQuery({
    start_time: showTomorrow 
      ? moment().add(1, 'day').startOf('day').toISOString()
      : moment().startOf('day').toISOString(),
    end_time: showTomorrow
      ? moment().add(1, 'day').endOf('day').toISOString()
      : moment().endOf('day').toISOString(),
  });
  const leadQuery = useGetLeadsQuery(
    {has_updates: true}, 
    {skip: !provider?.id}
  );
  const childProps = {
    ...props,
    provider,
    // appointments: get(appointmentsQuery, 'data.data', []),
    appointments: get(todaySessionsQuery, 'data.data', []).map(s => (
      s.appointment_scheduled ? {
        ...s.appointment_scheduled,
        session: s,
      } : 
      {
        patient: s.patient,
        provider: s.provider,
        start_time: s.date,
        session: s,
        is_canceled: s.is_canceled,
      }
    )),
    loading: (
      providerQuery.isLoading
      || appointmentsQuery.isLoading
    ),
    metricsSessionCount: get(sessionMetricsQuery, 'data.count'),
    metricsSessionPriorPeriodCount: (
      get(sessionMetricsPriorPeriodQuery, 'data.count')
    ),
    metricsSessionGoal: getSessionGoal({provider, metricsPeriod}),
    metricsLeadCount: get(leadQuery, 'data.count'),
    patientCount: get(patientsQuery, 'data.count') || 0,
    sessionCount: get(incompleteSessionsQuery, 'data.count') || 0,
    messageCount: get(messagesQuery, 'data.count') || 0,
    metricsPeriod,
    onMetricsPeriodChange: period => setMetricsPeriod(period),
    refetch: () => providerQuery.refetch(),
    showTomorrow,
    setShowTomorrow,
  };

  return (
    <AppDashboard
      title="Dashboard"
      loading={childProps.loading}
      showUser
    >
      {
        provider
        ? <ProviderHomeScreen {...childProps} />
        : null
      }
    </AppDashboard>
  );
};

export default ProviderHomePage;
